export const SLOW_AFTER_MS = 15_000;
export const SCRIPT_RETRY_DELAY_MS = 2_000;
export const WIDGET_RECOVERY_TIMEOUT_MS = 20_000;

export type TurnstileSurface = "sign_up" | "forgot_password";
export type TurnstilePhase =
  | "loading_script"
  | "rendering_widget"
  | "awaiting_token"
  | "recovering"
  | "token_ready"
  | "blocked";
export type TurnstileIncidentKind =
  | "slow"
  | "script_error"
  | "retryable_widget_error"
  | "configuration_error"
  | "interactive_timeout"
  | "unsupported"
  | "expired";
export type TurnstileSource = "script" | "widget" | "timeout" | "unsupported";
export type TurnstileRecoveryCause = "late_success" | "automatic_retry" | "manual_retry";

export type TurnstileIncident = {
  errorCode?: string;
  kind: TurnstileIncidentKind;
  retryable: boolean;
  source: TurnstileSource;
};

type TurnstileTimings = {
  scriptLoadMs?: number;
  tokenReadyMs?: number;
  totalTokenReadyMs?: number;
  widgetRenderMs?: number;
};

export type TurnstileState = {
  attempt: number;
  attemptStartedAt: number;
  autoScriptRetryUsed: boolean;
  currentIncident: TurnstileIncident | null;
  failureReported: boolean;
  hadIncident: boolean;
  hasWidget: boolean;
  manualRetryAttempted: boolean;
  manualRetryPending: boolean;
  pendingRecoveryCause: TurnstileRecoveryCause | null;
  phase: TurnstilePhase;
  readyReported: boolean;
  recoveryDeadlineAt: number | null;
  scriptGeneration: number;
  scriptLoadedAt: number | null;
  scriptStartedAt: number;
  slowReported: boolean;
  started: boolean;
  surface: TurnstileSurface;
  timings: TurnstileTimings;
  tokenReady: boolean;
  totalStartedAt: number;
  widgetLoadedAt: number | null;
};

export type TurnstileEvent =
  | { now: number; type: "start" }
  | { attemptStartedAt: number; now: number; type: "slow_timeout" }
  | { generation: number; now: number; type: "script_loaded" }
  | { generation: number; now: number; type: "script_error" }
  | { generation: number; now: number; type: "script_retry_started" }
  | { now: number; type: "widget_loaded" }
  | { errorCode?: string; now: number; type: "widget_error" }
  | { now: number; type: "widget_timeout" }
  | { now: number; type: "unsupported" }
  | { now: number; type: "expired" }
  | { hasWidget: boolean; now: number; type: "manual_retry" }
  | { deadline: number; now: number; type: "recovery_deadline" }
  | { now: number; type: "success" }
  | { now: number; type: "submission_reset" };

export type TurnstileEffect =
  | {
      event: "auth_turnstile_ready";
      properties: Record<string, boolean | number | string>;
      type: "analytics";
    }
  | {
      event: "auth_turnstile_slow";
      properties: Record<string, boolean | number | string>;
      type: "analytics";
    }
  | {
      event: "auth_turnstile_retry";
      properties: Record<string, boolean | number | string>;
      type: "analytics";
    }
  | {
      properties: Record<string, boolean | number | string>;
      type: "failure";
    }
  | {
      delayMs: number;
      generation: number;
      type: "schedule_script_retry";
    };

export type TurnstileTransition = {
  effects: TurnstileEffect[];
  state: TurnstileState;
};

const CONFIGURATION_ERROR_CODES = new Set([
  "110100",
  "110110",
  "110200",
  "200100",
  "400020",
  "400070",
]);
const integerDuration = (end: number, start: number) => Math.max(0, Math.round(end - start));

export const getTurnstileErrorFamily = (errorCode?: string) => {
  if (!errorCode || !/^\d{3}/.test(errorCode)) return "none";
  const family = errorCode.slice(0, 3);
  return ["110", "200", "300", "400", "600"].includes(family) ? family : "other";
};

export const classifyTurnstileError = (errorCode?: string) => {
  const normalizedCode = errorCode ? String(errorCode) : undefined;
  if (normalizedCode && CONFIGURATION_ERROR_CODES.has(normalizedCode)) {
    return { errorCode: normalizedCode, retryable: false };
  }
  return { errorCode: normalizedCode, retryable: true };
};

export const getTurnstileLanguage = (locale: string) => {
  const languages: Record<string, "da" | "en" | "nb" | "sv"> = {
    da: "da",
    en: "en",
    nb: "nb",
    nn: "nb",
    sv: "sv",
  };
  return languages[locale] ?? "en";
};

export const getAttemptBucket = (attempt: number) => {
  if (attempt <= 1) return "1";
  if (attempt === 2) return "2";
  return "3_plus";
};

export const createTurnstileState = (surface: TurnstileSurface): TurnstileState => ({
  attempt: 1,
  attemptStartedAt: 0,
  autoScriptRetryUsed: false,
  currentIncident: null,
  failureReported: false,
  hadIncident: false,
  hasWidget: false,
  manualRetryAttempted: false,
  manualRetryPending: false,
  pendingRecoveryCause: null,
  phase: "loading_script",
  readyReported: false,
  recoveryDeadlineAt: null,
  scriptGeneration: 0,
  scriptLoadedAt: null,
  scriptStartedAt: 0,
  slowReported: false,
  started: false,
  surface,
  timings: {},
  tokenReady: false,
  totalStartedAt: 0,
  widgetLoadedAt: null,
});

const timingProperties = (timings: TurnstileTimings) => {
  const properties: Record<string, number> = {};
  if (timings.scriptLoadMs !== undefined) properties.script_load_ms = timings.scriptLoadMs;
  if (timings.widgetRenderMs !== undefined) properties.widget_render_ms = timings.widgetRenderMs;
  if (timings.tokenReadyMs !== undefined) properties.token_ready_ms = timings.tokenReadyMs;
  if (timings.totalTokenReadyMs !== undefined) {
    properties.total_token_ready_ms = timings.totalTokenReadyMs;
  }
  return properties;
};

const automaticRecoveryCause = (state: TurnstileState): TurnstileRecoveryCause =>
  state.pendingRecoveryCause === "manual_retry" ? "manual_retry" : "automatic_retry";

const blockedTransition = (
  previousState: TurnstileState,
  incident: TurnstileIncident
): TurnstileTransition => {
  const state: TurnstileState = {
    ...previousState,
    currentIncident: incident,
    failureReported: true,
    hadIncident: true,
    manualRetryPending: false,
    phase: "blocked",
    recoveryDeadlineAt: null,
    tokenReady: false,
  };
  if (previousState.failureReported) return { effects: [], state };

  return {
    effects: [
      {
        properties: {
          attempt_bucket: getAttemptBucket(state.attempt),
          error_code: incident.errorCode ?? "none",
          error_family: getTurnstileErrorFamily(incident.errorCode),
          retryable: incident.retryable,
          source: incident.source,
          surface: state.surface,
          ...timingProperties(state.timings),
        },
        type: "failure",
      },
    ],
    state,
  };
};

export const transitionTurnstile = (
  state: TurnstileState,
  event: TurnstileEvent
): TurnstileTransition => {
  switch (event.type) {
    case "start": {
      if (state.started) return { effects: [], state };
      return {
        effects: [],
        state: {
          ...state,
          attemptStartedAt: event.now,
          scriptGeneration: 1,
          scriptStartedAt: event.now,
          started: true,
          totalStartedAt: event.now,
        },
      };
    }
    case "slow_timeout": {
      if (
        state.tokenReady ||
        state.attemptStartedAt !== event.attemptStartedAt ||
        event.now - state.attemptStartedAt < SLOW_AFTER_MS ||
        state.phase === "blocked"
      ) {
        return { effects: [], state };
      }
      const nextState: TurnstileState = {
        ...state,
        currentIncident: state.currentIncident ?? {
          kind: "slow",
          retryable: true,
          source: state.hasWidget ? "widget" : "script",
        },
        hadIncident: true,
        pendingRecoveryCause: state.pendingRecoveryCause ?? "late_success",
        slowReported: true,
      };
      if (state.slowReported) return { effects: [], state: nextState };
      return {
        effects: [
          {
            event: "auth_turnstile_slow",
            properties: {
              attempt_bucket: getAttemptBucket(state.attempt),
              phase: state.phase,
              surface: state.surface,
              ...timingProperties(state.timings),
            },
            type: "analytics",
          },
        ],
        state: nextState,
      };
    }
    case "script_loaded": {
      if (
        event.generation !== state.scriptGeneration ||
        state.phase !== "loading_script" ||
        state.tokenReady
      ) {
        return { effects: [], state };
      }
      const scriptLoadMs = integerDuration(event.now, state.scriptStartedAt);
      return {
        effects: [],
        state: {
          ...state,
          phase: "rendering_widget",
          scriptLoadedAt: event.now,
          timings: { ...state.timings, scriptLoadMs },
        },
      };
    }
    case "script_error": {
      if (
        event.generation !== state.scriptGeneration ||
        state.phase !== "loading_script" ||
        state.tokenReady
      ) {
        return { effects: [], state };
      }
      const incident: TurnstileIncident = {
        kind: "script_error",
        retryable: true,
        source: "script",
      };
      const pendingRecoveryCause = automaticRecoveryCause(state);
      if (state.autoScriptRetryUsed) {
        return blockedTransition(
          {
            ...state,
            pendingRecoveryCause,
            scriptGeneration: state.scriptGeneration + 1,
          },
          incident
        );
      }
      const generation = state.scriptGeneration + 1;
      return {
        effects: [
          {
            delayMs: SCRIPT_RETRY_DELAY_MS,
            generation,
            type: "schedule_script_retry",
          },
        ],
        state: {
          ...state,
          autoScriptRetryUsed: true,
          currentIncident: incident,
          hadIncident: true,
          pendingRecoveryCause,
          phase: "recovering",
          scriptGeneration: generation,
          scriptLoadedAt: null,
          tokenReady: false,
          widgetLoadedAt: null,
        },
      };
    }
    case "script_retry_started": {
      if (event.generation !== state.scriptGeneration || state.phase !== "recovering") {
        return { effects: [], state };
      }
      return {
        effects: [],
        state: {
          ...state,
          phase: "loading_script",
          scriptStartedAt: event.now,
        },
      };
    }
    case "widget_loaded": {
      if (state.tokenReady || state.phase !== "rendering_widget") {
        return { effects: [], state };
      }
      return {
        effects: [],
        state: {
          ...state,
          hasWidget: true,
          phase: "awaiting_token",
          timings: {
            ...state.timings,
            ...(state.scriptLoadedAt === null
              ? {}
              : { widgetRenderMs: integerDuration(event.now, state.scriptLoadedAt) }),
          },
          widgetLoadedAt: event.now,
        },
      };
    }
    case "widget_error": {
      if (state.tokenReady) return { effects: [], state };
      const classification = classifyTurnstileError(event.errorCode);
      if (state.phase === "blocked") {
        if (!state.currentIncident?.retryable || !classification.retryable) {
          return { effects: [], state };
        }
        return {
          effects: [],
          state: {
            ...state,
            currentIncident: {
              ...state.currentIncident,
              errorCode: classification.errorCode,
            },
          },
        };
      }
      const incident: TurnstileIncident = {
        errorCode: classification.errorCode,
        kind: classification.retryable ? "retryable_widget_error" : "configuration_error",
        retryable: classification.retryable,
        source: "widget",
      };
      const nextState: TurnstileState = {
        ...state,
        currentIncident: incident,
        hadIncident: true,
        pendingRecoveryCause: automaticRecoveryCause(state),
        tokenReady: false,
      };
      if (!classification.retryable) return blockedTransition(nextState, incident);
      return {
        effects: [],
        state: {
          ...nextState,
          phase: "recovering",
          recoveryDeadlineAt: state.recoveryDeadlineAt ?? event.now + WIDGET_RECOVERY_TIMEOUT_MS,
        },
      };
    }
    case "widget_timeout": {
      if (state.tokenReady || state.phase === "blocked") return { effects: [], state };
      return {
        effects: [],
        state: {
          ...state,
          currentIncident: {
            kind: "interactive_timeout",
            retryable: true,
            source: "timeout",
          },
          hadIncident: true,
          pendingRecoveryCause: automaticRecoveryCause(state),
          phase: "recovering",
          recoveryDeadlineAt: state.recoveryDeadlineAt ?? event.now + WIDGET_RECOVERY_TIMEOUT_MS,
          tokenReady: false,
        },
      };
    }
    case "unsupported": {
      if (state.tokenReady) return { effects: [], state };
      const incident: TurnstileIncident = {
        kind: "unsupported",
        retryable: false,
        source: "unsupported",
      };
      return blockedTransition(
        {
          ...state,
          pendingRecoveryCause: automaticRecoveryCause(state),
        },
        incident
      );
    }
    case "expired": {
      if (state.phase === "blocked") return { effects: [], state };
      return {
        effects: [],
        state: {
          ...state,
          attemptStartedAt: event.now,
          currentIncident: {
            kind: "expired",
            retryable: true,
            source: "widget",
          },
          hadIncident: true,
          manualRetryPending: false,
          pendingRecoveryCause: automaticRecoveryCause(state),
          phase: "awaiting_token",
          readyReported: false,
          recoveryDeadlineAt: null,
          tokenReady: false,
        },
      };
    }
    case "manual_retry": {
      const attempt = state.attempt + 1;
      const source = state.currentIncident?.source ?? (event.hasWidget ? "widget" : "script");
      return {
        effects: [
          {
            event: "auth_turnstile_retry",
            properties: {
              attempt_bucket: getAttemptBucket(attempt),
              source,
              surface: state.surface,
            },
            type: "analytics",
          },
        ],
        state: {
          ...state,
          attempt,
          attemptStartedAt: event.now,
          autoScriptRetryUsed: false,
          currentIncident: null,
          hadIncident: true,
          manualRetryAttempted: true,
          manualRetryPending: true,
          pendingRecoveryCause: "manual_retry",
          phase: event.hasWidget ? "recovering" : "loading_script",
          recoveryDeadlineAt: event.hasWidget ? event.now + WIDGET_RECOVERY_TIMEOUT_MS : null,
          scriptGeneration: event.hasWidget ? state.scriptGeneration : state.scriptGeneration + 1,
          scriptLoadedAt: event.hasWidget ? state.scriptLoadedAt : null,
          scriptStartedAt: event.hasWidget ? state.scriptStartedAt : event.now,
          timings: event.hasWidget ? state.timings : {},
          tokenReady: false,
          widgetLoadedAt: event.hasWidget ? state.widgetLoadedAt : null,
        },
      };
    }
    case "recovery_deadline": {
      if (
        state.tokenReady ||
        state.phase !== "recovering" ||
        state.recoveryDeadlineAt !== event.deadline ||
        event.now < event.deadline
      ) {
        return { effects: [], state };
      }
      return blockedTransition(
        state,
        state.currentIncident ?? {
          kind: "retryable_widget_error",
          retryable: true,
          source: "widget",
        }
      );
    }
    case "success": {
      const timings: TurnstileTimings = {
        ...state.timings,
        ...(state.widgetLoadedAt === null
          ? {}
          : { tokenReadyMs: integerDuration(event.now, state.widgetLoadedAt) }),
        totalTokenReadyMs: integerDuration(event.now, state.totalStartedAt),
      };
      const nextState: TurnstileState = {
        ...state,
        currentIncident: null,
        manualRetryAttempted: false,
        manualRetryPending: false,
        pendingRecoveryCause: null,
        phase: "token_ready",
        readyReported: true,
        recoveryDeadlineAt: null,
        timings,
        tokenReady: true,
      };
      if (state.readyReported) return { effects: [], state: nextState };
      return {
        effects: [
          {
            event: "auth_turnstile_ready",
            properties: {
              had_incident: state.hadIncident,
              ...(state.hadIncident && state.pendingRecoveryCause
                ? { recovery_cause: state.pendingRecoveryCause }
                : {}),
              surface: state.surface,
              ...timingProperties(timings),
            },
            type: "analytics",
          },
        ],
        state: nextState,
      };
    }
    case "submission_reset": {
      return {
        effects: [],
        state: {
          ...state,
          attemptStartedAt: event.now,
          currentIncident: null,
          manualRetryAttempted: false,
          manualRetryPending: false,
          pendingRecoveryCause: null,
          phase: state.hasWidget ? "awaiting_token" : "loading_script",
          readyReported: false,
          recoveryDeadlineAt: null,
          tokenReady: false,
        },
      };
    }
  }
};
